/* eslint-disable no-shadow */

import Vue from 'vue';
// eslint-disable-next-line import/no-cycle
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError, PMSError, HttpError } from '@/errors';

const endpoint = `${apiEndpoint}/inventory/extras`;

const state = {
  extras: null,
  payments: null,
  pending: false,
  error: null,
  pmsError: null,
  updatePending: false,
  updateError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.extras != null,
  extras: (state, getters) => (getters.loaded ? state.extras : []),
  payments: (state, getters) => (getters.loaded ? state.payments : []),
  noExtras: (state, getters) => getters.loaded && !getters.extras.length,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.extras = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
    state.pmsError = error;
  },
  data(state, { extras, payments }) {
    state.pending = false;
    state.extras = extras;
    state.payments = payments;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state, extra) {
    state.extras.push(JSON.parse(JSON.stringify(extra)));
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state, extra) {
    const idx = state.extras.findIndex(({ id }) => id === extra.id);
    if (idx !== -1) {
      Vue.set(state.extras, idx, JSON.parse(JSON.stringify(extra)));
    }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, id) {
    const idx = state.extras.findIndex((extra) => extra.id === id);
    if (idx !== -1) {
      state.extras.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
  sort(state, { field, order }) {
    if (field === 'langs.en.name') {
      state.extras.sort((a, b) => {
        const nameA = a.langs.en.name.toUpperCase(); // ignore upper and lowercase
        const nameB = b.langs.en.name.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return order ? -1 : 1;
        }
        if (nameA > nameB) {
          return order ? 1 : -1;
        }
        return 0;
      });
    }
    state.extras.sort((a, b) => {
      if (a[field] < b[field]) {
        return order ? -1 : 1;
      }
      if (a[field] > b[field]) {
        return order ? 1 : -1;
      }
      return 0;
    });
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }

  commit('update', err);
  throw err;
}

const actions = {
  async fetchData({ commit }, forced = false) {
    commit('beforeLoading', forced);
    try {
      const { data } = await ApiService.get(`${endpoint}`);
      commit('data', data);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async createExtra({ commit }, extra) {
    commit('beforeUpdate');
    try {
      let payload;
      if (!extra.cover) {
        payload = { ...extra };
      } else {
        payload = new FormData();
        payload.appendFromObject(extra);
      }
      const { data } = await ApiService.post(`${endpoint}`, payload);
      commit('created', data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateExtra({ commit }, extra) {
    commit('beforeUpdate');
    try {
      let payload;
      if (!extra.cover) {
        payload = { ...extra };
      } else {
        payload = new FormData();
        payload.appendFromObject(extra);
      }
      await ApiService.post(`${endpoint}/${extra.id}`, payload);
      commit('modified', { ...extra });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteExtra({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}`, { data: payload });
      commit('deleted', payload.id);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async getExtraByCultswitchId({ commit }, id) {
    try {
      const { data } = await ApiService.get(`${endpoint}/${id}`);
      return data;
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
